/**
 * Infer a schema from a JSON example output.
 *
 * Given a JSON object (usually a tool's example output), produce a list of
 * properties in the same shape ModelFormModal accepts. Nested arrays of
 * primitives become `list[str|int|...]`. Arrays of objects become
 * `list[<SuggestedChildClassName>]`.
 *
 * The function does not create child schemas - it returns property names and
 * types plus any suggested child schemas so the caller can decide how to
 * handle them (e.g., warn the user or build them too).
 */

export interface InferredProperty {
  name: string;
  type: string;
  listElementType?: string;
  literalValues?: string[];
}

export interface SuggestedChildSchema {
  /** Property name in the parent schema that references this child */
  parentPropertyName: string;
  /** Suggested class name for the child schema */
  suggestedClassName: string;
  /** Properties of the child schema */
  properties: InferredProperty[];
}

export interface InferenceResult {
  properties: InferredProperty[];
  /** Nested object/array-of-object shapes the user may want to create as child schemas */
  suggestedChildren: SuggestedChildSchema[];
}

/**
 * Capitalize a snake_case or kebab-case string into PascalCase for class names.
 */
const toPascalCase = (input: string): string => {
  return input
    .split(/[_\-\s]+/)
    .map((part) => (part.length === 0 ? '' : part.charAt(0).toUpperCase() + part.slice(1)))
    .join('');
};

/**
 * Convert a plural property name to singular for use as a child class name.
 * Simple heuristic - strips trailing 's' or 'es' when present. Not perfect,
 * but it's a suggestion the user can edit.
 */
const singularize = (name: string): string => {
  if (name.endsWith('ies') && name.length > 3) return `${name.slice(0, -3)}y`;
  if (name.endsWith('es') && name.length > 2) return name.slice(0, -2);
  if (name.endsWith('s') && name.length > 1) return name.slice(0, -1);
  return name;
};

/**
 * Infer the schema property type from a single value.
 */
const inferPrimitiveType = (value: unknown): string => {
  if (typeof value === 'string') return 'str';
  if (typeof value === 'boolean') return 'bool';
  if (typeof value === 'number') return Number.isInteger(value) ? 'int' : 'float';
  return 'str'; // fallback for null/undefined
};

/**
 * Infer properties from a single object. For nested object/array-of-object
 * fields, emits suggested child schemas.
 */
const inferFromObject = (
  obj: Record<string, unknown>,
  propertyNameForClass: string,
  suggestedChildren: SuggestedChildSchema[]
): InferredProperty[] => {
  const properties: InferredProperty[] = [];

  for (const [key, value] of Object.entries(obj)) {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        // Can't infer element type from empty list - default to str
        properties.push({ name: key, type: 'list', listElementType: 'str' });
        continue;
      }
      const first = value[0];
      if (first !== null && typeof first === 'object' && !Array.isArray(first)) {
        // Array of objects - suggest a child schema
        const childClassName = toPascalCase(singularize(key));
        const childProps = inferFromObject(
          first as Record<string, unknown>,
          childClassName,
          suggestedChildren
        );
        suggestedChildren.push({
          parentPropertyName: key,
          suggestedClassName: childClassName,
          properties: childProps,
        });
        properties.push({ name: key, type: 'list', listElementType: childClassName });
      } else {
        // Array of primitives
        properties.push({ name: key, type: 'list', listElementType: inferPrimitiveType(first) });
      }
    } else if (value !== null && typeof value === 'object') {
      // Nested object - suggest a child schema and use it as a Custom type
      const childClassName = toPascalCase(key);
      const childProps = inferFromObject(
        value as Record<string, unknown>,
        childClassName,
        suggestedChildren
      );
      suggestedChildren.push({
        parentPropertyName: key,
        suggestedClassName: childClassName,
        properties: childProps,
      });
      properties.push({ name: key, type: childClassName });
    } else {
      properties.push({ name: key, type: inferPrimitiveType(value) });
    }
  }

  return properties;
};

/**
 * Infer a schema from a JSON example string.
 *
 * @param jsonString - JSON example output
 * @param rootClassName - Suggested PascalCase class name for the root schema
 * @returns Inferred properties and any suggested nested child schemas
 * @throws Error if the input is not valid JSON
 */
export const inferSchemaFromJson = (
  jsonString: string,
  rootClassName: string
): InferenceResult => {
  const parsed = JSON.parse(jsonString);
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Example output must be a JSON object at the root');
  }

  const suggestedChildren: SuggestedChildSchema[] = [];
  const properties = inferFromObject(
    parsed as Record<string, unknown>,
    rootClassName,
    suggestedChildren
  );

  return { properties, suggestedChildren };
};
